import { PlanType } from "@brookbush/database";

import { useUser } from "@clerk/nextjs";
import clsx from "clsx";
import { motion } from "framer-motion";
import { XIcon } from "lucide-react";
import { useRouter } from "next/compat/router";
import Link from "next/link";
import posthog from "posthog-js";
import { useEffect, useRef } from "react";
import { flushSync } from "react-dom";
import { FaCheckCircle } from "react-icons/fa";
import { mutate as globalMutate } from "swr";

import Button from "~/components/Button";
import LayoutCard from "~/components/LayoutCard";
import { useAccess, useSubscriptionDB, useUserAction } from "~/hooks";
import useCheckout from "~/hooks/useCheckout";
import usePlanConfig from "~/hooks/usePlanConfig";
import captureBecomeAMemberEvent from "~/lib/captureBecomeAMemberEvent";

interface BecomeAMemberSuccessProps {
	isModal?: boolean;
	isOnboarding?: boolean;
}

const BecomeAMemberCheckoutSuccess = ({
	isModal,
	isOnboarding,
}: BecomeAMemberSuccessProps) => {
	const router = useRouter();
	const { planConfig, isLoadingPlanConfig } = usePlanConfig();
	const { resetUserAction } = useUserAction();
	const { refreshSubscriptionDB } = useSubscriptionDB();
	const { isMember } = useAccess();
	const { user } = useUser();
	const isMemberRef = useRef(isMember);
	isMemberRef.current = isMember;

	const { clearCheckout, paymentProvider } = useCheckout();

	// biome-ignore lint/correctness/useExhaustiveDependencies: report access once after post-checkout sync
	useEffect(() => {
		globalMutate("/onboarding/checklist");
		void user?.reload();
		void refreshSubscriptionDB();

		const pollIntervalMs = 1500;
		const reportAfterMs = 8000;
		const startedAt = Date.now();

		const pollTimer = window.setInterval(() => {
			if (isMemberRef.current || Date.now() - startedAt >= reportAfterMs) {
				window.clearInterval(pollTimer);
				return;
			}
			void refreshSubscriptionDB();
			void user?.reload();
		}, pollIntervalMs);

		const reportTimer = window.setTimeout(() => {
			window.clearInterval(pollTimer);
			posthog.capture("membership:access_check", {
				is_member: isMemberRef.current,
				surface: isModal ? "modal" : "page",
				provider: paymentProvider ?? undefined,
			});
		}, reportAfterMs);

		return () => {
			window.clearInterval(pollTimer);
			window.clearTimeout(reportTimer);
		};
		// Report access after polling so transient post-pay races are less noisy.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		captureBecomeAMemberEvent("completed", {
			surface: isModal ? "modal" : "page",
			interval: planConfig.price?.recurring?.interval,
			promo_code: planConfig.promoCode?.code,
			price_id: planConfig.price?.id,
			provider: paymentProvider ?? undefined,
		});
		// Capture once when the success screen mounts.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			{isModal && (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1, transition: { duration: 0.3 } }}
					exit={{ opacity: 0, transition: { duration: 0.2 } }}
					onClick={() => {
						resetUserAction();
						clearCheckout();
					}}
					className="fixed inset-0 z-50 bg-black/40 dark:bg-black/50"
					aria-hidden="true"
				/>
			)}

			<LayoutCard
				title="Payment Successful"
				isLoading={isLoadingPlanConfig}
				hasEnterExitAnimations={isModal}
				className={clsx({
					"fixed inset-0 z-50 mt-auto h-min max-h-[90dvh] overflow-scroll rounded-b-none sm:m-auto sm:max-h-[95vh] sm:max-w-md sm:rounded-xl":
						isModal,
				})}
				subtitle="Congratulations, You're a member now."
			>
				{isModal && (
					<button
						onClick={() => {
							resetUserAction();
							clearCheckout();
						}}
						className="absolute top-4 right-0 mr-3 cursor-pointer rounded-full bg-slate-100 p-1 transition-all hover:brightness-95 sm:-right-1 sm:mr-5 sm:block dark:bg-slate-700"
					>
						<XIcon className="h-5 phablet:h-6 phablet:w-6 w-5" />
					</button>
				)}

				<div className="flex flex-col items-center gap-12">
					<FaCheckCircle size={100} className="mt-4 text-emerald-500" />

					<div className="flex w-full flex-col gap-4">
						{planConfig.planType === PlanType.TEAM && (
							<Link href={"/membership/team"}>
								<Button className="w-full" variant="outlined">
									Manage My Team
								</Button>
							</Link>
						)}

						<Button
							className="w-full"
							onClick={async () => {
								if (isModal) {
									resetUserAction();
									clearCheckout();
								} else {
									flushSync(() => {
										router?.push("/");
									});
								}
							}}
						>
							{isModal ? "Dismiss" : "Continue"}
						</Button>

						{isOnboarding && !isModal && (
							<Button
								variant="outlined"
								className="w-full"
								onClick={async () => {
									await router?.push("/membership/add-payment-method");

									posthog.capture("onboarding_become_a_member_completed");

									clearCheckout();
								}}
							>
								Add Backup Payment Method
							</Button>
						)}
					</div>
				</div>
			</LayoutCard>
		</>
	);
};

export default BecomeAMemberCheckoutSuccess;
